import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, MonitorSmartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { ChangePasswordButton } from "@/components/ChangePasswordButton";
import { TwoFactorSetup } from "@/components/TwoFactorSetup";
import { fetchTotpStatus, revokeTrustedDevice, fetchMe } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const shortUA = (ua) => {
  if (!ua) return "Appareil inconnu";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac OS X/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows";
  return ua.slice(0, 40);
};

// "Sécurité" card: password change + two-factor authentication.
const SecurityCard = () => {
  const { setUser } = useAuth();
  const qc = useQueryClient();
  const [setupOpen, setSetupOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const { data: status } = useQuery({ queryKey: ["totp-status"], queryFn: fetchTotpStatus });
  const enabled = !!status?.enabled;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["totp-status"] });
    fetchMe().then(setUser).catch(() => {});
  };

  const revoke = async (id) => {
    try {
      await revokeTrustedDevice(id);
      toast.success("Appareil révoqué");
      refresh();
    } catch {
      toast.error("Impossible de révoquer cet appareil");
    }
  };

  return (
    <div data-testid="security-card" className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-heading font-semibold text-slate-800 text-sm">Sécurité</p>
          <p className="text-xs text-slate-400 mt-0.5">Mot de passe de connexion · 12 caractères minimum.</p>
        </div>
        <ChangePasswordButton />
      </div>
      <div className="border-t border-slate-100" />
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-heading font-semibold text-slate-800 text-sm">Double authentification</p>
            <span
              data-testid="totp-status-badge"
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${enabled ? "text-emerald-700 bg-emerald-50" : "text-slate-500 bg-slate-100"}`}
            >
              {enabled ? "Activée" : "Désactivée"}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            {enabled
              ? `${status.backup_codes_remaining} code${status.backup_codes_remaining > 1 ? "s" : ""} de secours restant${status.backup_codes_remaining > 1 ? "s" : ""}.`
              : "Code à 6 chiffres demandé à chaque connexion, en plus du mot de passe."}
          </p>
        </div>
        {enabled ? (
          <button
            type="button"
            data-testid="totp-manage-button"
            onClick={() => setManageOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:border-emerald-300 hover:text-emerald-700 transition-colors shrink-0"
          >
            <ShieldCheck className="h-4 w-4" /> Gérer
          </button>
        ) : (
          <button
            type="button"
            data-testid="totp-enable-button"
            onClick={() => setSetupOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:border-emerald-300 hover:text-emerald-700 transition-colors shrink-0"
          >
            <ShieldCheck className="h-4 w-4" /> Activer la double authentification
          </button>
        )}
      </div>

      {/* Setup dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Double authentification</DialogTitle>
            <DialogDescription className="sr-only">Activer la double authentification sur votre compte</DialogDescription>
          </DialogHeader>
          <TwoFactorSetup onEnabled={() => { setSetupOpen(false); refresh(); }} />
        </DialogContent>
      </Dialog>

      {/* Manage dialog: backup codes count + trusted devices */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Double authentification</DialogTitle>
            <DialogDescription className="sr-only">Gérer les appareils de confiance</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {status?.backup_codes_remaining ?? 0} code{(status?.backup_codes_remaining ?? 0) > 1 ? "s" : ""} de secours restant{(status?.backup_codes_remaining ?? 0) > 1 ? "s" : ""}.
            </p>
            <div>
              <p className="text-xs tracking-[0.06em] uppercase font-semibold text-slate-400 mb-2">Appareils de confiance</p>
              {(status?.trusted_devices || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic" data-testid="trusted-devices-empty">Aucun appareil de confiance.</p>
              ) : (
                <div className="space-y-1.5">
                  {status.trusted_devices.map((d) => (
                    <div key={d.id} data-testid={`trusted-device-${d.id}`} className="flex items-center gap-2.5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                      <MonitorSmartphone className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1 leading-tight">
                        <p className="text-sm font-medium text-slate-800 truncate">{shortUA(d.user_agent)}</p>
                        <p className="text-[11px] text-slate-400">
                          expire le {new Date(d.expires_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={`revoke-device-${d.id}`}
                        onClick={() => revoke(d.id)}
                        aria-label="Révoquer cet appareil"
                        className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-white shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Blocks every signed-in member must see next to "Mon poste": push
// notifications toggle + password change + two-factor authentication.
// Rendered in Réglages > Postes (admins) and on the /mon-poste page (agents).
export const AccountBlocks = () => (
  <>
    <PushNotificationsCard />
    <SecurityCard />
  </>
);
